import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { crearOrden, confirmarOrden } from '../services/api';
import { useTranslation } from 'react-i18next';
import { translateError } from '../services/api';

const PAYPAL_CLIENT_ID = 'AZ-U69Xu85NrpuokdK4Lr9i1746lT2bd5WtOd9cny4YdoMAWoCV_VNTeXZkBj9OIC-GRIFizeQzj1Qs3';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
  padding: 2rem;
`;

const Card = styled.div`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 500px;
  margin: 2rem auto;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 0.25rem;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  margin: 0 0 2rem;
  font-size: 0.95rem;
`;

const SummaryBox = styled.div`
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
`;

const SummaryTitle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #94a3b8;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.3rem 0;
  font-size: 0.95rem;
  color: #cbd5e1;
`;

const SummaryTotal = styled(SummaryRow)`
  font-size: 1.1rem;
  font-weight: 700;
  color: #e2e8f0;
  border-top: 1px solid #2d2d5e;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1.5rem;
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.9rem;
  &:hover { color: #e2e8f0; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  color: #475569;
  font-size: 0.85rem;
  &::before, &::after { content: ''; flex: 1; border-top: 1px solid #2d2d5e; }
`;

const TransferBtn = styled.button`
  width: 100%;
  background: #f0c14b;
  color: #111;
  border: 1px solid #d4a835;
  border-radius: 12px;
  padding: 0.85rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:hover { opacity: 0.9; }
`;

const PayPalContainer = styled.div`
  margin-top: 1rem;
  min-height: 60px;
`;

const ErrorMsg = styled.p`
  color: #ef4444;
  font-size: 0.9rem;
  margin: 0.75rem 0 0;
  text-align: center;
`;

const ProcessingMsg = styled.div`
  text-align: center;
  color: #a78bfa;
  font-size: 0.95rem;
  padding: 1rem 0;
`;

interface LocationState {
  eventData: { name: string; eventDate: string; description: string; moderationEnabled: boolean; };
  plan: string;
  originalAmount: number;
  discountPct?: number;
  finalAmount: number;
  couponCode?: string;
}

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state as LocationState | null;
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!state) {
    return (
      <Wrapper>
        <Card>
          <p style={{ color: '#ef4444' }}>{t('payment.session_expired')} <Link to="/dashboard/nuevo-evento" style={{ color: '#7c3aed' }}>{t('payment.session_link')}</Link></p>
        </Card>
      </Wrapper>
    );
  }

  const { eventData, plan, originalAmount, discountPct, finalAmount, couponCode } = state;

  const handleCreateOrder = async (_data: any, actions: any) => {
    setError('');
    const { ordenId } = await crearOrden({
      plan, originalAmountMxn: originalAmount, discountPct, finalAmountMxn: finalAmount,
      couponCode, eventName: eventData.name, eventDate: eventData.eventDate,
      eventDescription: eventData.description, moderationEnabled: eventData.moderationEnabled,
    });
    return actions.order.create({
      purchase_units: [{
        amount: { value: finalAmount.toFixed(2), currency_code: 'MXN' },
        description: `FotoVibe — Plan ${plan}`,
        custom_id: ordenId.toString(),
      }],
      application_context: {
        return_url: 'https://fotovibe.mx/app/dashboard',
        cancel_url: 'https://fotovibe.mx/app/dashboard/nuevo-evento',
        landing_page: 'LOGIN', user_action: 'PAY_NOW', shipping_preference: 'NO_SHIPPING',
      },
    });
  };

  const handleApprove = async (_data: any, actions: any) => {
    try {
      setProcesando(true);
      const details = await actions.order.capture();
      const ordenId = parseInt(details.purchase_units[0].custom_id, 10);
      const captureId = details.purchase_units?.[0]?.payments?.captures?.[0]?.id || details.id;
      const { eventId } = await confirmarOrden(ordenId, captureId);
      navigate(`/dashboard/evento/${eventId}`, { state: { justCreated: true } });
    } catch (e: any) {
      setError(translateError(e, t('payment.err_confirm')));
      setProcesando(false);
    }
  };

  const paypalOptions = { clientId: PAYPAL_CLIENT_ID, currency: 'MXN', intent: 'capture' as const, components: 'buttons' };

  return (
    <Wrapper>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <BackLink to="/dashboard/nuevo-evento">{t('payment.back')}</BackLink>
      </div>
      <Card>
        <Title>{t('payment.title')}</Title>
        <Subtitle>{t('payment.sub')}</Subtitle>

        <SummaryBox>
          <SummaryTitle>{t('payment.summary_title')}</SummaryTitle>
          <SummaryRow>
            <span>{t('payment.row_event')}</span>
            <span style={{ maxWidth: '200px', textAlign: 'right', wordBreak: 'break-word' }}>{eventData.name}</span>
          </SummaryRow>
          <SummaryRow>
            <span>{t('payment.row_plan', { name: plan })}</span>
            <span>${originalAmount.toFixed(2)} MXN</span>
          </SummaryRow>
          {discountPct && discountPct > 0 && (
            <SummaryRow style={{ color: '#10b981' }}>
              <span>{t('payment.row_discount', { code: couponCode, pct: discountPct })}</span>
              <span>-${(originalAmount - finalAmount).toFixed(2)} MXN</span>
            </SummaryRow>
          )}
          <SummaryTotal>
            <span>{t('payment.row_total')}</span>
            <span style={{ color: '#7c3aed' }}>${finalAmount.toFixed(2)} MXN</span>
          </SummaryTotal>
        </SummaryBox>

        {procesando ? (
          <ProcessingMsg>{t('payment.processing')}</ProcessingMsg>
        ) : (
          <>
            <PayPalContainer>
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 50 }}
                  createOrder={handleCreateOrder}
                  onApprove={handleApprove}
                  onError={() => setError(t('payment.err_paypal'))}
                  onCancel={() => setError(t('payment.err_cancel'))}
                  forceReRender={[finalAmount]}
                />
              </PayPalScriptProvider>
            </PayPalContainer>

            <Divider>o</Divider>

            <TransferBtn type="button" onClick={() => window.open('/app/transferencia', '_blank')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
              {t('payment.btn_transfer')}
            </TransferBtn>
          </>
        )}

        {error && <ErrorMsg>{error}</ErrorMsg>}
      </Card>
    </Wrapper>
  );
};

export default PaymentPage;
